import type { ReactNode } from 'react';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import type { ImageSourcePropType } from 'react-native';

import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';

export type VideoPreparationProps = {
  title: string;
  actionLabel: string;
  image: ImageSourcePropType;
  aspectRatio: number;
  testID: string;
  inverse?: boolean;
  footer?: ReactNode;
};

export function VideoPreparation({
  title,
  actionLabel,
  image,
  aspectRatio,
  testID,
  inverse = false,
  footer,
}: VideoPreparationProps) {
  const theme = useTheme();
  const color = inverse ? theme.colors.textInverse : theme.colors.text;

  return (
    <View
      testID={testID}
      style={[
        styles.container,
        {
          paddingHorizontal: theme.spacing(3),
          paddingTop: theme.spacing(12),
          paddingBottom: theme.spacing(14),
        },
      ]}
    >
      <ScrollView
        nestedScrollEnabled
        style={styles.scroll}
        contentContainerStyle={[styles.content, { gap: theme.spacing(2) }]}
      >
        <Text
          variant="title"
          weight="semibold"
          color={color}
          accessibilityRole="header"
          style={styles.centered}
          testID="incident-video-status-text"
        >
          {title}
        </Text>
        {actionLabel !== title ? (
          <Text variant="label" color={color} style={styles.centered}>
            {actionLabel}
          </Text>
        ) : null}
        <Image
          source={image}
          accessibilityLabel={actionLabel}
          resizeMode="contain"
          style={[styles.image, { aspectRatio }]}
        />
        {footer ? <View style={styles.footer}>{footer}</View> : null}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0, width: '100%' },
  scroll: { flex: 1, minHeight: 0, width: '100%' },
  content: { alignItems: 'center' },
  image: { flexShrink: 0, width: '100%', maxWidth: 300 },
  centered: { textAlign: 'center', flexShrink: 0 },
  footer: { alignItems: 'center', flexShrink: 0 },
});
